import { AI_ASSETS, AI_OBJECTS, GEOLOGY_ASSETS, GEOLOGY_OBJECTS, HUMAN_ASSETS, HUMAN_OBJECTS, HUMAN_SITES, START_AREA_HALF_SIZE, TERRAIN_CHUNK_SIZE, WORLD_HALF_SIZE, PLAYER_RADIUS, VEGETATION_ASSETS, VEGETATION_OBJECTS, getCollisionBroadphaseCandidatesAt, resolveStaticCollider, resolveWorldCollision, } from "./world.js";
import { WORLD_CHUNK_SIZE, createStableWorldObjectId, generateWorldChunk, generateWorldPoisInBounds, getWorldHighwayProfileAt, getWorldHeightAt, getWorldTerrainNormalAt, isWorldChunkInBounds, worldToChunkCoordinate, } from "./world-generation.js";
import { getInteractableDescriptor } from "./interaction.js";
import { PUBLIC_LIGHT_OBJECTS } from "./world-light-sources.js";
const humanPlacements = (values) => Object.freeze(values);
const aiPlacements = (values) => Object.freeze(values);
const HUMAN_BLUEPRINTS = Object.freeze({
    abandoned_checkpoint: humanPlacements([
        { assetId: "concrete_barrier_01", x: -4, z: -2 },
        { assetId: "concrete_barrier_01", x: 4, z: -2 },
        { assetId: "road_sign_01", x: 5.8, z: 3, yaw: Math.PI },
        { assetId: "wreck_car_01", x: -8, z: 7, yaw: 0.32 },
        { assetId: "small_bunker_01", x: 12, z: 8, yaw: 0.12, embedding: 0.28 },
        { assetId: "lamp_post_broken_01", x: -6, z: 12 },
        { assetId: "concrete_rubble_01", x: 9, z: 3, terrainAlignment: 0.7 },
    ]),
    small_ruin: humanPlacements([
        { assetId: "ruined_corner_01", x: 0, z: 0, yaw: 0.08 },
        { assetId: "ruined_concrete_wall_01", x: 4.2, z: 0, yaw: 0.05 },
        { assetId: "collapsed_slab_01", x: 1, z: 3.6, yaw: -0.35, terrainAlignment: 0.45 },
        { assetId: "concrete_rubble_01", x: -3.2, z: 2.8, terrainAlignment: 0.7 },
        { assetId: "metal_debris_01", x: 3.4, z: -2.7, yaw: 0.4, terrainAlignment: 0.65 },
    ]),
    roadside_site: humanPlacements([
        { assetId: "wreck_car_01", x: 0, z: 0, yaw: 0.18 },
        { assetId: "road_sign_01", x: -4.8, z: 1.5 },
        { assetId: "utility_pole_01", x: 6, z: -5, yaw: -0.1 },
        { assetId: "concrete_barrier_01", x: -3.5, z: 5, yaw: 0.22 },
        { assetId: "metal_debris_01", x: 3.7, z: 3.2, terrainAlignment: 0.65 },
    ]),
    suburban_ruin: humanPlacements([
        { assetId: "house_ruin_01", x: -27, z: -25, yaw: 0.03 },
        { assetId: "house_ruin_02", x: 28, z: -24, yaw: -0.04 },
        { assetId: "small_shop_ruin_01", x: -38, z: 31, yaw: 0.05 },
        { assetId: "garage_ruin_01", x: 31, z: 27, yaw: -0.06 },
        { assetId: "parking_lot_01", x: -38, z: 14, yaw: 0.02, embedding: -0.03, terrainAlignment: 0.96 },
        { assetId: "apartment_shell_01", x: -59, z: -24, yaw: 0.02 },
        { assetId: "commercial_shell_01", x: 60, z: 27, yaw: -0.03 },
        { assetId: "lamp_post_broken_01", x: -7, z: -3 },
        { assetId: "utility_pole_01", x: 8, z: 3 },
        { assetId: "wreck_car_01", x: -34, z: 14, yaw: 0.08 },
        { assetId: "wreck_car_01", x: -42, z: 12, yaw: -0.12 },
        { assetId: "concrete_rubble_02", x: -7, z: -15, terrainAlignment: 0.72 },
        { assetId: "metal_debris_01", x: 9, z: 16, terrainAlignment: 0.66 },
    ]),
    industrial_site: humanPlacements([
        { assetId: "factory_hall_01", x: -16, z: -23, yaw: 0.03 },
        { assetId: "warehouse_01", x: 17, z: 23, yaw: -0.04 },
        { assetId: "smokestack_01", x: -35, z: 20 },
        { assetId: "industrial_pipe_run_01", x: 0, z: 16, yaw: 0.05 },
        { assetId: "storage_tank_group_01", x: 27, z: -19, yaw: -0.08 },
        { assetId: "substation_01", x: -7, z: 25 },
        { assetId: "parking_lot_01", x: 10, z: -31, embedding: -0.03, terrainAlignment: 0.96 },
        { assetId: "wreck_truck_01", x: 10, z: -31, yaw: 0.12 },
        { assetId: "fence_segment_01", x: -31, z: -35 },
        { assetId: "fence_segment_01", x: -26, z: -35 },
        { assetId: "concrete_rubble_01", x: 1, z: -3, terrainAlignment: 0.72 },
        { assetId: "metal_debris_01", x: 12, z: 8, terrainAlignment: 0.65 },
    ]),
    gas_station: humanPlacements([
        { assetId: "road_access_lane_01", x: 0, z: -17, embedding: -0.03, terrainAlignment: 0.96 },
        { assetId: "parking_lot_01", x: 0, z: 10, embedding: -0.03, terrainAlignment: 0.96 },
        { assetId: "gas_station_canopy_01", x: 0, z: 0 },
        { assetId: "fuel_pump_01", x: -3.2, z: 0 },
        { assetId: "fuel_pump_01", x: 0, z: 0 },
        { assetId: "fuel_pump_01", x: 3.2, z: 0 },
        { assetId: "convenience_store_shell_01", x: 0, z: 24 },
        { assetId: "highway_sign_01", x: -18, z: -9, yaw: Math.PI * 0.5 },
        { assetId: "wreck_car_01", x: 11, z: 8, yaw: 0.35 },
        { assetId: "wreck_truck_01", x: -13, z: 11, yaw: -0.18 },
        { assetId: "guardrail_01", x: -9, z: -14 },
        { assetId: "guardrail_01", x: 0, z: -14 },
        { assetId: "guardrail_01", x: 9, z: -14 },
    ]),
    ai_farm: humanPlacements([
        { assetId: "greenhouse_01", x: -24, z: -15 },
        { assetId: "greenhouse_01", x: 0, z: -15 },
        { assetId: "greenhouse_01", x: 24, z: -15 },
        { assetId: "farm_service_building_01", x: -24, z: 18 },
        { assetId: "farm_water_tank_01", x: -8, z: 19 },
        { assetId: "irrigation_pipe_01", x: 10, z: 4 },
        { assetId: "irrigation_pipe_01", x: 24, z: 4 },
        { assetId: "crop_row_01", x: 7, z: 17 },
        { assetId: "crop_row_01", x: 20, z: 17 },
        { assetId: "crop_row_01", x: 33, z: 17 },
        { assetId: "fence_segment_01", x: -35, z: -29 },
        { assetId: "fence_segment_01", x: -30, z: -29 },
    ]),
});
const AI_BLUEPRINTS = Object.freeze({
    wetware_relay: aiPlacements([
        { assetId: "wetware_datacenter_01", x: 0, z: 0 },
        { assetId: "wetware_vat_01", x: 8, z: 1, scale: 0.7 },
        { assetId: "wetware_node_01", x: 7, z: 5, scale: 0.85 },
        { assetId: "wetware_tissue_cluster_01", x: 5, z: 2.8, scale: 0.7, terrainAlignment: 0.08 },
        { assetId: "ai_relay_node_01", x: -8, z: -5 },
        { assetId: "ai_power_interface_01", x: 0, z: -7 },
        { assetId: "wetware_conduit_01", x: -4, z: -5, yaw: Math.PI * 0.5, terrainAlignment: 0.94 },
        { assetId: "wetware_conduit_01", x: 4, z: -6, yaw: Math.PI * 0.5, terrainAlignment: 0.94 },
        { assetId: "ai_sensor_pole_01", x: -10, z: 8 },
        { assetId: "ai_sensor_pole_01", x: 11, z: -8 },
    ]),
    sensor_site: aiPlacements([
        { assetId: "ai_sensor_pole_01", x: 0, z: 0 },
        { assetId: "ai_ground_sensor_01", x: 4, z: 2, terrainAlignment: 0.82 },
        { assetId: "ai_ground_sensor_01", x: -4, z: -3, terrainAlignment: 0.82 },
        { assetId: "ai_relay_node_01", x: 1, z: -5, scale: 0.72 },
        { assetId: "wetware_conduit_01", x: 0, z: -2.5, terrainAlignment: 0.94 },
    ]),
    excavation_site: aiPlacements([
        { assetId: "autonomous_excavator_01", x: 0, z: 0 },
        { assetId: "autonomous_excavator_01", x: 14, z: -9, yaw: 0.5, scale: 0.82 },
        { assetId: "ai_sensor_pole_01", x: -13, z: -10 },
        { assetId: "ai_sensor_pole_01", x: 15, z: 12 },
        { assetId: "ai_ground_sensor_01", x: -7, z: 9, terrainAlignment: 0.82 },
        { assetId: "ai_power_interface_01", x: 7, z: 8 },
        { assetId: "wetware_conduit_01", x: 3, z: 6, yaw: Math.PI * 0.5, terrainAlignment: 0.94 },
    ]),
    ai_farm: aiPlacements([
        { assetId: "ai_sensor_pole_01", x: -35, z: -25 },
        { assetId: "ai_sensor_pole_01", x: 35, z: -25 },
        { assetId: "ai_sensor_pole_01", x: 35, z: 25 },
        { assetId: "ai_sensor_pole_01", x: -35, z: 25 },
        { assetId: "ai_power_interface_01", x: -10, z: 27 },
        { assetId: "ai_relay_node_01", x: 0, z: 28 },
        { assetId: "ai_ground_sensor_01", x: 18, z: 27, terrainAlignment: 0.82 },
    ]),
    ecological_monitor: aiPlacements([
        { assetId: "ai_sensor_pole_01", x: 0, z: 0 },
        { assetId: "ai_ground_sensor_01", x: 4, z: 3, terrainAlignment: 0.82 },
        { assetId: "ai_ground_sensor_01", x: -5, z: -2, terrainAlignment: 0.82 },
        { assetId: "ai_relay_node_01", x: 1, z: -6, scale: 0.72 },
    ]),
});
/** Dominant wetware country stays sparse; the seeded macro relay carries the full facility language. */
const WETWARE_AMBIENT_SENSOR_BLUEPRINT = aiPlacements(AI_BLUEPRINTS.sensor_site.slice(0, 3));
function randomFromId(id, salt) {
    let hash = 2_166_136_261 ^ salt;
    for (const character of id) {
        hash ^= character.charCodeAt(0);
        hash = Math.imul(hash, 16_777_619);
    }
    return (hash >>> 0) / 4_294_967_295;
}
function isInStartMacro(x, z) {
    return Math.max(Math.abs(x), Math.abs(z)) <= START_AREA_HALF_SIZE;
}
function belongsToStreamChunk(x, z, chunkX, chunkZ) {
    return Math.floor(x / WORLD_CHUNK_SIZE) === chunkX && Math.floor(z / WORLD_CHUNK_SIZE) === chunkZ;
}
const STARTING_CHECKPOINT = HUMAN_SITES.find((site) => site.id === "human-site:abandoned-checkpoint:01");
export const STARTING_SUPPLY_CACHE_LOCAL_PLACEMENT = Object.freeze({
    localX: -9,
    localZ: 13,
    localYaw: 1.35,
});
const startingSupplyCacheCosine = Math.cos(STARTING_CHECKPOINT.rotation);
const startingSupplyCacheSine = Math.sin(STARTING_CHECKPOINT.rotation);
const startingSupplyCacheX = STARTING_CHECKPOINT.x
    + STARTING_SUPPLY_CACHE_LOCAL_PLACEMENT.localX * startingSupplyCacheCosine
    + STARTING_SUPPLY_CACHE_LOCAL_PLACEMENT.localZ * startingSupplyCacheSine;
const startingSupplyCacheZ = STARTING_CHECKPOINT.z
    - STARTING_SUPPLY_CACHE_LOCAL_PLACEMENT.localX * startingSupplyCacheSine
    + STARTING_SUPPLY_CACHE_LOCAL_PLACEMENT.localZ * startingSupplyCacheCosine;
export const STARTING_SUPPLY_CACHE_OBJECT_ID = createStableWorldObjectId({
    kind: "human-supply-cache",
    chunkX: worldToChunkCoordinate(startingSupplyCacheX),
    chunkZ: worldToChunkCoordinate(startingSupplyCacheZ),
    localIndex: "starting-checkpoint-cache",
    siteId: STARTING_CHECKPOINT.id,
});
export const STARTING_SUPPLY_CACHE_OBJECT = Object.freeze({
    id: STARTING_SUPPLY_CACHE_OBJECT_ID,
    siteId: STARTING_CHECKPOINT.id,
    assetId: "supply_cache_closed",
    category: HUMAN_ASSETS.supply_cache_closed.category,
    x: startingSupplyCacheX,
    y: getWorldHeightAt(startingSupplyCacheX, startingSupplyCacheZ) - 0.03,
    z: startingSupplyCacheZ,
    yaw: STARTING_CHECKPOINT.rotation + STARTING_SUPPLY_CACHE_LOCAL_PLACEMENT.localYaw,
    scale: 1,
    tint: -0.018,
    terrainNormal: getWorldTerrainNormalAt(startingSupplyCacheX, startingSupplyCacheZ, 0.7),
    terrainAlignment: 0,
    chunkX: Math.floor(startingSupplyCacheX / TERRAIN_CHUNK_SIZE),
    chunkZ: Math.floor(startingSupplyCacheZ / TERRAIN_CHUNK_SIZE),
});
function geologyFromCandidate(candidate) {
    const roll = randomFromId(candidate.id, 11);
    let assetId;
    if (candidate.archetype === "formation")
        assetId = "rock_formation_01";
    else if (candidate.archetype === "boulder")
        assetId = roll < 0.5 ? "boulder_large_01" : "boulder_large_02";
    else if (candidate.archetype === "medium")
        assetId = roll < 0.5 ? "rock_medium_01" : "rock_medium_02";
    else
        assetId = roll < 0.5 ? "rock_small_01" : "rock_small_02";
    const definition = GEOLOGY_ASSETS[assetId];
    const scale = candidate.scale * (candidate.archetype === "formation" ? 1.16 : 1);
    return Object.freeze({
        id: candidate.id,
        assetId,
        category: definition.category,
        x: candidate.x,
        y: candidate.y - (candidate.archetype === "formation" ? 0.2 : 0.05) * scale,
        z: candidate.z,
        scale,
        yaw: candidate.rotation,
        terrainNormal: getWorldTerrainNormalAt(candidate.x, candidate.z, 0.8),
        normalAlignment: candidate.archetype === "formation" ? 0.18 : 0.42,
        tint: (randomFromId(candidate.id, 12) - 0.5) * 0.13,
        collisionRadius: definition.collisionRadius * scale,
        chunkX: Math.floor(candidate.x / TERRAIN_CHUNK_SIZE),
        chunkZ: Math.floor(candidate.z / TERRAIN_CHUNK_SIZE),
    });
}
function vegetationFromCandidate(candidate) {
    const roll = randomFromId(candidate.id, 21);
    let assetId;
    if (candidate.archetype === "burned_tree")
        assetId = roll < 0.55 ? "tree_burned_01" : "tree_broken_01";
    else if (candidate.archetype === "sparse_tree")
        assetId = "tree_sparse_01";
    else if (candidate.archetype === "dead_shrub")
        assetId = roll < 0.5 ? "shrub_dead_01" : "shrub_dead_02";
    else if (candidate.archetype === "living_tree")
        assetId = roll < 0.5 ? "living_tree_01" : "living_tree_02";
    else if (candidate.archetype === "living_shrub")
        assetId = "shrub_living_01";
    else if (candidate.archetype === "grass")
        assetId = "grass_cluster_01";
    else if (candidate.archetype === "nuclear_tree")
        assetId = roll < 0.4
            ? "nuclear_tree_tall_01"
            : roll < 0.78
                ? "nuclear_tree_tall_02"
                : "nuclear_tree_massive_01";
    else if (candidate.archetype === "dead_giant")
        assetId = "nuclear_tree_dead_giant_01";
    else if (candidate.archetype === "fallen_log")
        assetId = "fallen_log_large_01";
    else if (candidate.archetype === "understory")
        assetId = "forest_understory_cluster_01";
    else
        assetId = roll < 0.34 ? "tree_dead_01" : roll < 0.67 ? "tree_dead_02" : "tree_dead_03";
    const definition = VEGETATION_ASSETS[assetId];
    const groundCover = definition.category === "dead_shrub"
        || definition.category === "living_shrub"
        || definition.category === "grass"
        || definition.category === "understory";
    const scale = candidate.scale * (groundCover ? 0.9 : 1);
    return Object.freeze({
        id: candidate.id,
        assetId,
        category: definition.category,
        x: candidate.x,
        y: candidate.y - (groundCover ? 0.015 : 0.035) * scale,
        z: candidate.z,
        scale,
        yaw: candidate.rotation,
        tint: (randomFromId(candidate.id, 22) - 0.5) * 0.1,
        collisionRadius: definition.collisionRadius * scale,
        chunkX: Math.floor(candidate.x / TERRAIN_CHUNK_SIZE),
        chunkZ: Math.floor(candidate.z / TERRAIN_CHUNK_SIZE),
    });
}
function placeHumanSite(site, blueprint) {
    const cosine = Math.cos(site.rotation);
    const sine = Math.sin(site.rotation);
    return Object.freeze(blueprint.map((placed, index) => {
        const x = site.x + placed.x * cosine - placed.z * sine;
        const z = site.z + placed.x * sine + placed.z * cosine;
        const scale = placed.scale ?? 1;
        const definition = HUMAN_ASSETS[placed.assetId];
        return Object.freeze({
            id: createStableWorldObjectId("human-structure", site.streamChunkX, site.streamChunkZ, index, site.id),
            siteId: site.id,
            assetId: placed.assetId,
            category: definition.category,
            x,
            y: getWorldHeightAt(x, z) - (placed.embedding ?? 0.06) * scale,
            z,
            yaw: site.rotation + (placed.yaw ?? 0),
            scale,
            tint: (randomFromId(`${site.id}:${index}`, 31) - 0.5) * 0.065,
            terrainNormal: getWorldTerrainNormalAt(x, z, 0.7),
            terrainAlignment: placed.terrainAlignment ?? (definition.category === "rubble" || definition.category === "road_damage" ? 0.7 : 0),
            chunkX: Math.floor(x / TERRAIN_CHUNK_SIZE),
            chunkZ: Math.floor(z / TERRAIN_CHUNK_SIZE),
        });
    }));
}
function placeAiSite(site, blueprint) {
    const cosine = Math.cos(site.rotation);
    const sine = Math.sin(site.rotation);
    return Object.freeze(blueprint.map((placed, index) => {
        const x = site.x + placed.x * cosine - placed.z * sine;
        const z = site.z + placed.x * sine + placed.z * cosine;
        const scale = placed.scale ?? 1;
        const definition = AI_ASSETS[placed.assetId];
        return Object.freeze({
            id: createStableWorldObjectId("ai-structure", site.streamChunkX, site.streamChunkZ, index, site.id),
            siteId: site.id,
            assetId: placed.assetId,
            category: definition.category,
            x,
            y: getWorldHeightAt(x, z) - (placed.embedding ?? 0.06) * scale,
            z,
            yaw: site.rotation + (placed.yaw ?? 0),
            scale,
            tint: (randomFromId(`${site.id}:${index}`, 41) - 0.5) * 0.036,
            terrainNormal: getWorldTerrainNormalAt(x, z, 0.7),
            terrainAlignment: placed.terrainAlignment ?? (definition.category === "conduit" ? 0.94 : 0),
            collision: definition.collision,
            active: true,
            sensorCapable: definition.sensorCapable,
            networked: definition.networked,
            chunkX: Math.floor(x / TERRAIN_CHUNK_SIZE),
            chunkZ: Math.floor(z / TERRAIN_CHUNK_SIZE),
        });
    }));
}
function highwayHumanObject(source, assetId, x, z, yaw, localIndex, embedding, terrainAlignment) {
    const definition = HUMAN_ASSETS[assetId];
    const { x: ownerX, z: ownerZ } = source.metadata.chunk;
    const siteId = createStableWorldObjectId("highway-infrastructure", ownerX, ownerZ, "heritage-route");
    return Object.freeze({
        id: createStableWorldObjectId(`highway-${assetId}`, ownerX, ownerZ, localIndex, siteId),
        siteId,
        assetId,
        category: definition.category,
        x,
        y: getWorldHeightAt(x, z) - embedding,
        z,
        yaw,
        scale: 1,
        tint: (randomFromId(`${siteId}:${localIndex}`, 37) - 0.5) * 0.035,
        terrainNormal: getWorldTerrainNormalAt(x, z, 0.7),
        terrainAlignment,
        chunkX: Math.floor(x / TERRAIN_CHUNK_SIZE),
        chunkZ: Math.floor(z / TERRAIN_CHUNK_SIZE),
    });
}
/** Lane paint and sparse shoulders make the global corridor read as a highway. */
function addHighwayInfrastructure(source, human) {
    const bounds = source.metadata.bounds;
    const firstSegment = Math.ceil((bounds.minX - 32) / 16);
    const finalSegment = Math.floor((bounds.maxX + 32 - 0.0001) / 16);
    const pushIfOwned = (object) => {
        if (belongsToStreamChunk(object.x, object.z, source.metadata.chunk.x, source.metadata.chunk.z))
            human.push(object);
    };
    for (let segment = firstSegment; segment <= finalSegment; segment += 1) {
        const parameterX = segment * 16;
        const highway = getWorldHighwayProfileAt(parameterX);
        if (highway.z < bounds.minZ - 32 || highway.z >= bounds.maxZ + 32)
            continue;
        if (Math.max(Math.abs(parameterX), Math.abs(highway.z)) <= 320)
            continue;
        pushIfOwned(highwayHumanObject(source, "highway_lane_marking_01", parameterX, highway.z, highway.heading, `lane:${segment}`, -0.025, 0.96));
        if (segment % 2 !== 0 || (parameterX >= 960 && parameterX <= 1_200))
            continue;
        const shoulderOffset = highway.width * 0.58;
        const normalX = -Math.sin(highway.heading);
        const normalZ = Math.cos(highway.heading);
        for (const side of [-1, 1]) {
            const x = parameterX + normalX * shoulderOffset * side;
            const z = highway.z + normalZ * shoulderOffset * side;
            pushIfOwned(highwayHumanObject(source, "guardrail_01", x, z, highway.heading, `shoulder:${segment}:${side}`, 0.04, 0.28));
        }
    }
}
export function generateWorldObjectChunk(chunkX, chunkZ) {
    const source = generateWorldChunk(chunkX, chunkZ);
    const nearbySites = generateWorldPoisInBounds({
        minX: source.metadata.bounds.minX - 160,
        minZ: source.metadata.bounds.minZ - 160,
        maxX: source.metadata.bounds.maxX + 160,
        maxZ: source.metadata.bounds.maxZ + 160,
    });
    const geology = GEOLOGY_OBJECTS.filter((object) => belongsToStreamChunk(object.x, object.z, chunkX, chunkZ));
    const vegetation = VEGETATION_OBJECTS.filter((object) => belongsToStreamChunk(object.x, object.z, chunkX, chunkZ));
    for (const candidate of source.geologyCandidates)
        if (!isInStartMacro(candidate.x, candidate.z))
            geology.push(geologyFromCandidate(candidate));
    for (const candidate of source.vegetationCandidates)
        if (!isInStartMacro(candidate.x, candidate.z))
            vegetation.push(vegetationFromCandidate(candidate));
    const human = HUMAN_OBJECTS.filter((object) => belongsToStreamChunk(object.x, object.z, chunkX, chunkZ));
    for (const light of PUBLIC_LIGHT_OBJECTS) {
        if (belongsToStreamChunk(light.x, light.z, chunkX, chunkZ))
            human.push(light);
    }
    if (belongsToStreamChunk(STARTING_SUPPLY_CACHE_OBJECT.x, STARTING_SUPPLY_CACHE_OBJECT.z, chunkX, chunkZ)) {
        human.push(STARTING_SUPPLY_CACHE_OBJECT);
    }
    const ai = AI_OBJECTS.filter((object) => belongsToStreamChunk(object.x, object.z, chunkX, chunkZ));
    for (const site of nearbySites) {
        if (site.origin !== "human")
            continue;
        if (site.authored)
            continue;
        human.push(...placeHumanSite(site, HUMAN_BLUEPRINTS[site.archetype] ?? HUMAN_BLUEPRINTS.small_ruin).filter((object) => (belongsToStreamChunk(object.x, object.z, chunkX, chunkZ))));
    }
    for (const site of nearbySites) {
        if (site.origin !== "ai")
            continue;
        if (site.authored)
            continue;
        if (site.archetype === "ai_farm")
            human.push(...placeHumanSite(site, HUMAN_BLUEPRINTS.ai_farm).filter((object) => (belongsToStreamChunk(object.x, object.z, chunkX, chunkZ))));
        ai.push(...placeAiSite(site, AI_BLUEPRINTS[site.archetype] ?? AI_BLUEPRINTS.sensor_site).filter((object) => (belongsToStreamChunk(object.x, object.z, chunkX, chunkZ))));
    }
    addHighwayInfrastructure(source, human);
    const interactables = [...geology, ...vegetation, ...human, ...ai]
        .map((object) => getInteractableDescriptor(object))
        .filter((descriptor) => descriptor !== undefined);
    return Object.freeze({
        source,
        geology: Object.freeze(geology),
        vegetation: Object.freeze(vegetation),
        human: Object.freeze(human),
        ai: Object.freeze(ai),
        interactables: Object.freeze(interactables),
    });
}
const LEGACY_OBJECT_IDS = new Set([
    ...GEOLOGY_OBJECTS.map((object) => object.id),
    ...VEGETATION_OBJECTS.map((object) => object.id),
    ...HUMAN_OBJECTS.map((object) => object.id),
    ...AI_OBJECTS.map((object) => object.id),
]);
export const CLIENT_COLLIDER_CACHE_LIMIT = 81;
/** Two complete 100-player spread working sets plus boundary handoff headroom. */
export const SERVER_COLLIDER_CACHE_LIMIT = 2_048;
const generatedColliderCache = new Map();
function humanCollider(object) {
    const collision = HUMAN_ASSETS[object.assetId].collision;
    const base = { id: object.id, category: "human", x: object.x, z: object.z, fallbackAngle: object.yaw };
    if (collision.shape === "none")
        return undefined;
    if (collision.shape === "circle")
        return Object.freeze({ ...base, shape: "circle", radius: collision.radius * object.scale });
    if (collision.shape === "box")
        return Object.freeze({
            ...base,
            shape: "box",
            halfWidth: collision.halfWidth * object.scale,
            halfDepth: collision.halfDepth * object.scale,
            yaw: object.yaw,
        });
    return Object.freeze({
        ...base,
        shape: "capsule",
        radius: collision.radius * object.scale,
        halfLength: collision.halfLength * object.scale,
        yaw: object.yaw,
    });
}
function aiCollider(object) {
    const collision = object.collision;
    const base = { id: object.id, category: "ai", x: object.x, z: object.z, fallbackAngle: object.yaw };
    if (collision.shape === "none")
        return undefined;
    if (collision.shape === "circle")
        return Object.freeze({ ...base, shape: "circle", radius: collision.radius * object.scale });
    if (collision.shape === "box")
        return Object.freeze({
            ...base,
            shape: "box",
            halfWidth: collision.halfWidth * object.scale,
            halfDepth: collision.halfDepth * object.scale,
            yaw: object.yaw,
        });
    return Object.freeze({
        ...base,
        shape: "capsule",
        radius: collision.radius * object.scale,
        halfLength: collision.halfLength * object.scale,
        yaw: object.yaw,
    });
}
function generatedCollidersForChunk(chunkX, chunkZ, cache, cacheLimit) {
    if (!isWorldChunkInBounds(chunkX, chunkZ))
        return [];
    const key = `${chunkX}:${chunkZ}`;
    const existing = cache.get(key);
    if (existing) {
        cache.delete(key);
        cache.set(key, existing);
        return existing;
    }
    const content = generateWorldObjectChunk(chunkX, chunkZ);
    const colliders = [];
    for (const object of content.geology) {
        if (LEGACY_OBJECT_IDS.has(object.id) || object.collisionRadius <= 0)
            continue;
        colliders.push(Object.freeze({
            id: object.id,
            category: "geology",
            x: object.x,
            z: object.z,
            shape: "circle",
            radius: object.collisionRadius,
            fallbackAngle: object.yaw,
        }));
    }
    for (const object of content.vegetation) {
        if (LEGACY_OBJECT_IDS.has(object.id) || object.collisionRadius <= 0)
            continue;
        colliders.push(Object.freeze({
            id: object.id,
            category: "tree",
            x: object.x,
            z: object.z,
            shape: "circle",
            radius: object.collisionRadius,
            fallbackAngle: object.yaw,
        }));
    }
    for (const object of content.human) {
        if (LEGACY_OBJECT_IDS.has(object.id))
            continue;
        const collider = humanCollider(object);
        if (collider)
            colliders.push(collider);
    }
    for (const object of content.ai) {
        if (LEGACY_OBJECT_IDS.has(object.id))
            continue;
        const collider = aiCollider(object);
        if (collider)
            colliders.push(collider);
    }
    const frozen = Object.freeze(colliders);
    cache.set(key, frozen);
    while (cache.size > cacheLimit) {
        const oldest = cache.keys().next().value;
        if (oldest === undefined)
            break;
        cache.delete(oldest);
    }
    return frozen;
}
function generatedCollisionCandidatesAt(x, z, cache, cacheLimit, queryRadius = 0) {
    const centerX = worldToChunkCoordinate(x);
    const centerZ = worldToChunkCoordinate(z);
    const candidates = [];
    for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
        for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
            for (const collider of generatedCollidersForChunk(centerX + offsetX, centerZ + offsetZ, cache, cacheLimit)) {
                const broadRadius = collider.shape === "circle"
                    ? collider.radius
                    : collider.shape === "box"
                        ? Math.hypot(collider.halfWidth, collider.halfDepth)
                        : collider.radius + collider.halfLength;
                if (Math.hypot(collider.x - x, collider.z - z) <= broadRadius + PLAYER_RADIUS + 2 + queryRadius)
                    candidates.push(collider);
            }
        }
    }
    return candidates;
}
function resolveLargeWorldCollisionWithCache(targetX, targetZ, cache, cacheLimit, probe) {
    const legacyProbe = probe ? { candidateCount: 0 } : undefined;
    let [x, z] = resolveWorldCollision(targetX, targetZ, legacyProbe);
    if (probe) {
        probe.candidateCount = legacyProbe?.candidateCount ?? 0;
        delete probe.contact;
        if (legacyProbe?.contact)
            probe.contact = legacyProbe.contact;
    }
    for (let pass = 0; pass < 6; pass += 1) {
        let moved = false;
        for (const collider of generatedCollisionCandidatesAt(x, z, cache, cacheLimit)) {
            if (probe)
                probe.candidateCount += 1;
            const [nextX, nextZ] = resolveStaticCollider(x, z, collider);
            const distance = Math.hypot(nextX - x, nextZ - z);
            if (distance > 1e-7) {
                moved = true;
                if (probe && !probe.contact)
                    probe.contact = { normalX: (nextX - x) / distance,
                        normalZ: (nextZ - z) / distance, obstacleId: collider.id, category: collider.category };
            }
            x = nextX;
            z = nextZ;
        }
        if (!moved)
            break;
    }
    const limit = WORLD_HALF_SIZE - PLAYER_RADIUS;
    return [Math.max(-limit, Math.min(limit, x)), Math.max(-limit, Math.min(limit, z))];
}
/** Isolated bounded resolver lets authority retain more active player cells than one client. */
export class LargeWorldCollisionResolver {
    cacheLimit;
    cache = new Map();
    constructor(cacheLimit = CLIENT_COLLIDER_CACHE_LIMIT) {
        this.cacheLimit = cacheLimit;
        if (!Number.isInteger(cacheLimit) || cacheLimit < 9)
            throw new Error("Collision cache limit must be an integer of at least nine chunks");
    }
    get cacheSize() {
        return this.cache.size;
    }
    resolve(targetX, targetZ, probe) {
        return resolveLargeWorldCollisionWithCache(targetX, targetZ, this.cache, this.cacheLimit, probe);
    }
    candidatesAt(x, z) {
        return Object.freeze([
            ...getCollisionBroadphaseCandidatesAt(x, z),
            ...generatedCollisionCandidatesAt(x, z, this.cache, this.cacheLimit),
        ]);
    }
}
/** Shared deterministic large-world collision used by client prediction. */
export function resolveLargeWorldCollision(targetX, targetZ, probe) {
    return resolveLargeWorldCollisionWithCache(targetX, targetZ, generatedColliderCache, CLIENT_COLLIDER_CACHE_LIMIT, probe);
}
/** Optional bounded radius includes the nearby third-person camera boom. */
export function getLargeWorldCollisionCandidatesAt(x, z, queryRadius = 0) {
    return Object.freeze([
        ...getCollisionBroadphaseCandidatesAt(x, z),
        ...generatedCollisionCandidatesAt(x, z, generatedColliderCache, CLIENT_COLLIDER_CACHE_LIMIT, Number.isFinite(queryRadius) ? Math.max(0, Math.min(16, queryRadius)) : 0),
    ]);
}
export function getGeneratedCollisionCacheSize() {
    return generatedColliderCache.size;
}
